// Hoja de ruta del viaje (PP-41): lo que el proveedor le pasa al chofer por
// WhatsApp, impresa o por telefono. Un solo modelo (`RouteSheet`) alimenta la
// hoja en pantalla, la impresion y el texto para compartir, asi las tres dicen
// exactamente lo mismo y nada mas.
//
// Los nombres y domicilios de pasajeros salen solo si la API los mando (viaje
// asignado a la empresa y vigente, PP-61 y PP-69); aca no se completan ni se
// infieren. Nunca un id tecnico: el viaje, la parada, el vehiculo y el
// conductor se nombran como los ve una persona.
//
// PP-65 ("Copiar datos del viaje" para el sistema propio del proveedor) es
// otro formato sobre el mismo modelo (`dispatchFields`): reordena lo que ya
// dice la hoja para los campos de un sistema de despacho, sin sumar datos.
import type { ProviderTripDetailResponse, ProviderTripPassenger, ProviderTripSummary } from "@ucorp/contracts";
import { VEHICLE_TYPE_LABEL } from "./fleet.js";
import { formatClock, formatDate, stopNames } from "./requests.js";

/** Vehiculo asignado tal como figura en la nomina de la empresa. */
export interface RouteSheetVehicle {
  readonly code: string;
  readonly plate: string;
}

/** Conductor asignado tal como figura en la nomina de la empresa. */
export interface RouteSheetDriver {
  readonly code: string;
  readonly name: string;
}

export interface RouteSheetInput {
  readonly detail: ProviderTripDetailResponse;
  /** Nombre de la planta de la habilitacion (nunca su id). */
  readonly siteName: string;
  /** Ausente: sin asignar o fuera de la nomina que tiene cargada el portal. */
  readonly vehicle?: RouteSheetVehicle;
  readonly driver?: RouteSheetDriver;
}

export interface RouteSheetStop {
  /** Clave de lista: el orden de la parada, nunca su id. */
  readonly key: string;
  /** Hora planificada en la zona de la planta (HH:MM). */
  readonly time: string;
  /** "Origen", "Parada 2", "Planta (destino)". */
  readonly name: string;
  /** La planta no lleva renglon de pasajeros. */
  readonly site: boolean;
  /** "1 pasajero", "3 pasajeros". Ausente en la planta. */
  readonly count?: string;
  /** Solo lo que mando la API; vacio si no mando nombres. */
  readonly passengers: readonly ProviderTripPassenger[];
}

export interface RouteSheetField {
  readonly label: string;
  readonly value: string;
}

export interface RouteSheet {
  readonly title: string;
  /** Los mismos valores que "Planta" y "Fecha" en `fields`, para otros formatos. */
  readonly siteName: string;
  readonly date: string;
  /** Planta, fecha, servicio, vehiculo y conductor, en ese orden. */
  readonly fields: readonly RouteSheetField[];
  readonly stops: readonly RouteSheetStop[];
  /** Si la hoja trae nombres o domicilios: la UI lo advierte al compartir. */
  readonly personal: boolean;
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? `1 ${one}` : `${String(n)} ${many}`);

function directionLabel(direction: ProviderTripSummary["direction"]): string {
  if (direction === "inbound") return "Ingreso";
  if (direction === "outbound") return "Egreso";
  return "Traslado";
}

export const NOT_ASSIGNED = "Sin asignar";
const SERVICE_LABEL = "Servicio";

export function buildRouteSheet(input: RouteSheetInput): RouteSheet {
  const { detail } = input;
  const { trip, timezone } = detail;
  const names = stopNames(detail.stops, trip.summary?.direction);
  const ordered = [...detail.stops].sort((a, b) => a.order - b.order);
  const stops: RouteSheetStop[] = ordered.map((stop) => {
    const site = stop.kind === "site" || (names.get(stop.stopId)?.startsWith("Planta") ?? false);
    return {
      key: String(stop.order),
      time: formatClock(stop.estimatedPassTime, timezone),
      name: names.get(stop.stopId) ?? "Parada",
      site,
      ...(site ? {} : { count: plural(stop.passengerCount, "pasajero", "pasajeros") }),
      passengers: site ? [] : (stop.passengers ?? []),
    };
  });
  const vehicle = input.vehicle === undefined ? NOT_ASSIGNED : `${input.vehicle.code} · ${input.vehicle.plate}`;
  const driver = input.driver === undefined ? NOT_ASSIGNED : `${input.driver.code} · ${input.driver.name}`;
  const date = formatDate(trip.date);
  return {
    title: "Hoja de ruta",
    siteName: input.siteName,
    date,
    fields: [
      { label: "Planta", value: input.siteName },
      { label: "Fecha", value: date },
      { label: SERVICE_LABEL, value: `${VEHICLE_TYPE_LABEL[trip.vehicleType]} · ${directionLabel(trip.summary?.direction)}` },
      { label: "Vehículo", value: vehicle },
      { label: "Conductor", value: driver },
    ],
    stops,
    personal: stops.some((stop) => stop.passengers.length > 0),
  };
}

/** Renglon de la hoja: titulo, dato, parada, pasajero o separador. */
export type RouteSheetLine =
  | { readonly kind: "title"; readonly text: string }
  | { readonly kind: "field"; readonly label: string; readonly value: string }
  | { readonly kind: "stop"; readonly time: string; readonly name: string; readonly count?: string }
  | { readonly kind: "passenger"; readonly name: string; readonly address?: string }
  | { readonly kind: "blank" };

/** Armazon comun de todo formato de texto: el orden y el contenido salen de aca. */
export function routeSheetLines(sheet: RouteSheet): RouteSheetLine[] {
  const lines: RouteSheetLine[] = [{ kind: "title", text: sheet.title }];
  for (const field of sheet.fields) lines.push({ kind: "field", label: field.label, value: field.value });
  lines.push({ kind: "blank" });
  for (const stop of sheet.stops) {
    lines.push({ kind: "stop", time: stop.time, name: stop.name, ...(stop.count !== undefined ? { count: stop.count } : {}) });
    for (const p of stop.passengers) lines.push({ kind: "passenger", name: p.name, ...(p.address !== undefined ? { address: p.address } : {}) });
  }
  return lines;
}

/** Texto de una parada, igual en pantalla y en el texto compartible. */
export function stopHeading(stop: Pick<RouteSheetStop, "time" | "name" | "count">): string {
  return [stop.time, stop.name, stop.count].filter((part): part is string => part !== undefined && part !== "").join(" · ");
}

/** Texto de un pasajero, igual en pantalla y en el texto compartible. */
export function passengerText(passenger: ProviderTripPassenger): string {
  return passenger.address === undefined ? passenger.name : `${passenger.name}, ${passenger.address}`;
}

export type RouteSheetTextFormat = "whatsapp" | "plain";

/**
 * Texto para pegar. `whatsapp` marca el titulo y las paradas en negrita con
 * asteriscos; `plain` va sin marcas. Mismos datos que la hoja, nada mas.
 */
export function routeSheetText(sheet: RouteSheet, format: RouteSheetTextFormat = "whatsapp"): string {
  const bold = (text: string): string => (format === "whatsapp" ? `*${text}*` : text);
  return routeSheetLines(sheet)
    .map((line) => {
      switch (line.kind) {
        case "title":
          return bold(line.text);
        case "field":
          return `${line.label}: ${line.value}`;
        case "stop":
          return bold(stopHeading(line));
        case "passenger":
          return `- ${passengerText(line)}`;
        case "blank":
          return "";
      }
    })
    .join("\n");
}

// --- Datos del viaje para el sistema propio del proveedor (PP-65) -----------
//
// Un sistema de despacho tipico acepta hora, origen y destino, y el resto va
// en un campo de comentarios. El formato sale de la hoja ya armada: mismos
// datos, otro orden. Vehiculo y conductor no van: el sistema propio asigna su
// movil y despues se registra en UCORP por codigo.

/** Campos del texto para el sistema propio, en el orden en que se pegan. */
export interface DispatchField {
  readonly label: "Fecha" | "Hora" | "Origen" | "Destino" | "Servicio" | "Comentarios";
  readonly value: string;
}

/** Separador de paradas en la linea de comentarios: corto y raro en un domicilio. */
export const DISPATCH_STOP_SEPARATOR = " | ";

/**
 * Como se escribe un extremo del viaje: la planta por su nombre; una parada
 * por el domicilio de sus pasajeros si la API lo mando (sin repetir), y si no
 * por su nombre y cantidad, igual que en la hoja.
 */
function endpoint(stop: RouteSheetStop, siteName: string): string {
  if (stop.site) return siteName;
  const addresses = [...new Set(stop.passengers.flatMap((p) => (p.address === undefined ? [] : [p.address])))];
  if (addresses.length > 0) return addresses.join(" / ");
  return stop.count === undefined ? stop.name : `${stop.name} · ${stop.count}`;
}

/** Una parada en la linea de comentarios: "06:20 Parada 2: Ana, Calle 1; Bruno". */
function compactStop(stop: RouteSheetStop): string {
  const head = [stop.time, stop.name].filter((part) => part !== "").join(" ");
  if (stop.passengers.length === 0) return stop.count === undefined ? head : `${head} (${stop.count})`;
  return `${head}: ${stop.passengers.map(passengerText).join("; ")}`;
}

/**
 * Hora (la de la primera parada: el primer retiro o la salida de planta),
 * origen y destino (primera y ultima parada), y en comentarios cada parada con
 * pasajeros en una sola linea. La planta no va en comentarios: ya es origen o
 * destino. Sin paradas no hay nada que copiar.
 */
export function dispatchFields(sheet: RouteSheet): DispatchField[] {
  const first = sheet.stops[0];
  const last = sheet.stops[sheet.stops.length - 1];
  if (first === undefined || last === undefined) return [];
  const service = sheet.fields.find((field) => field.label === SERVICE_LABEL)?.value;
  const comments = sheet.stops.filter((stop) => !stop.site).map(compactStop);
  const fields: DispatchField[] = [
    { label: "Fecha", value: sheet.date },
    { label: "Hora", value: first.time },
    { label: "Origen", value: endpoint(first, sheet.siteName) },
  ];
  if (last !== first) fields.push({ label: "Destino", value: endpoint(last, sheet.siteName) });
  if (service !== undefined) fields.push({ label: "Servicio", value: service });
  if (comments.length > 0) fields.push({ label: "Comentarios", value: comments.join(DISPATCH_STOP_SEPARATOR) });
  return fields.filter((field) => field.value !== "");
}

/** Texto plano, un campo por renglon, para pegar campo por campo en el sistema propio. */
export function dispatchText(sheet: RouteSheet): string {
  return dispatchFields(sheet)
    .map((field) => `${field.label}: ${field.value}`)
    .join("\n");
}
